import React from "react";
import { Phone, Handshake, Shirt, Sparkles, ZoomIn, BookOpen, Users, TrendingUp } from "lucide-react";

const IMG = (f) => `/landing/online/${f}`;

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="mb-3 flex items-center justify-center gap-2">
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-navy-900 text-yellow-300 shadow"><Icon className="h-4 w-4" /></span>
      <span className="font-display text-xs font-extrabold uppercase tracking-widest text-navy-900 sm:text-sm">{children}</span>
    </div>
  );
}

// Founder PMM mini profile
export function FounderCard({ ms, setLightbox }) {
  const L = (m, e) => (ms ? m : e);
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 shadow-xl" data-testid="online-founder-card">
      <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-yellow-600 via-yellow-300 to-yellow-600" />
      <div className="grid grid-cols-5 items-end">
        <button type="button" onClick={() => setLightbox(IMG("poster-a.jpg"))} className="col-span-2 self-end" data-testid="online-founder-photo">
          <img src={IMG("founder.jpg")} alt="Founder PMM" className="h-auto w-full transition-transform duration-500 hover:scale-105" loading="lazy" />
        </button>
        <div className="col-span-3 space-y-2 p-4">
          <p className="font-display text-[10px] font-extrabold uppercase tracking-[0.2em] text-yellow-300">Setta Group Holdings</p>
          <p className="font-display text-2xl font-extrabold uppercase leading-none text-white">Founder <span className="text-yellow-300">PMM</span></p>
          <p className="font-script text-xl leading-tight text-yellow-100">{L("\u201CSetiap Anak Boleh Membaca\u201D", "\u201CEvery Child Can Read\u201D")}</p>
          <div className="space-y-1 pt-1">
            {[
              { icon: BookOpen, t: L("Membaca Ilmu", "Reading Is Knowledge") },
              { icon: Users, t: L("Mengubah Masa Depan", "Changing The Future") },
              { icon: TrendingUp, t: L("Generasi Lebih Hebat", "A Greater Generation") },
            ].map((x) => (
              <div key={x.t} className="flex items-center gap-2 text-[11px] font-bold uppercase text-white/90">
                <span className="grid h-6 w-6 place-items-center rounded-full ring-1 ring-yellow-400 text-yellow-300"><x.icon className="h-3 w-3" /></span>{x.t}
              </div>
            ))}
          </div>
          <a href="tel:+60167202689" className="mt-1 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 px-3 py-1.5 font-display text-xs font-extrabold text-navy-950 shadow-md transition-transform hover:scale-105" data-testid="online-founder-hotline">
            <Phone className="h-3.5 w-3.5" /> <span className="num">016-7202689</span>
          </a>
        </div>
      </div>
    </div>
  );
}

const SCHOOLS = [
  { img: "school-rinting.jpg", name: "Sekolah Rendah Taman Rinting 3", place: "Masai, Johor", ms: "Pelaksanaan Program Membaca PMM", en: "PMM Reading Program rollout" },
  { img: "school-larkin.jpg", name: "Kelas Pendidikan Khas SK Larkin 2", place: "Johor Bahru", ms: "Program PMM di kelas pendidikan khas", en: "PMM in special education class" },
  { img: "school-kinarut.jpg", name: "Seminar Bersama Ibubapa", place: "SK Kinarut, K. Kinabalu, Sabah", ms: "Seminar ibubapa di sekolah", en: "Parents' seminar at school" },
];

// School collaborations (trust strip)
export function KerjasamaStrip({ ms, setLightbox }) {
  const L = (m, e) => (ms ? m : e);
  return (
    <div data-testid="online-kerjasama">
      <SectionTitle icon={Handshake}>{L("Kerjasama Bersama Sekolah", "School Collaborations")}</SectionTitle>
      <div className="grid grid-cols-3 gap-2">
        {SCHOOLS.map((s) => (
          <button key={s.img} type="button" onClick={() => setLightbox(IMG("poster-b.jpg"))} className="group overflow-hidden rounded-xl border-2 border-yellow-400/70 bg-white text-left shadow-md transition-transform duration-300 hover:-translate-y-1" data-testid={`online-school-${s.img.split("-")[1].split(".")[0]}`}>
            <div className="overflow-hidden">
              <img src={IMG(s.img)} alt={s.name} className="aspect-[4/5] w-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
            </div>
            <div className="bg-gradient-to-b from-yellow-100 to-yellow-200 p-2">
              <p className="font-display text-[9px] font-extrabold uppercase leading-tight text-navy-950 sm:text-[11px]">{s.name}</p>
              <p className="mt-0.5 text-[9px] font-semibold text-navy-800 sm:text-[10px]">{s.place}</p>
            </div>
          </button>
        ))}
      </div>
      <p className="mt-2 text-center font-script text-lg text-navy-800">{L("Bersama Melahirkan Anak Malaysia Yang Lancar Membaca", "Raising Malaysian Children Who Read Fluently")}</p>
    </div>
  );
}

const SIZES = ["4 - 5", "6 - 7", "8 - 9", "10 - 11", "12 - 15"];

// Official PMM jersey
export function JerseyBlock({ ms, setLightbox }) {
  const L = (m, e) => (ms ? m : e);
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-navy-200 bg-gradient-to-br from-white to-navy-50 shadow-md" data-testid="online-jersey">
      <div className="grid grid-cols-5 items-center gap-3 p-3">
        <button type="button" onClick={() => setLightbox(IMG("poster-c.jpg"))} className="col-span-2 overflow-hidden rounded-xl shadow-lg" data-testid="online-jersey-photo">
          <img src={IMG("jersey-kids.jpg")} alt={L("Jersi rasmi Program Membaca PMM", "Official PMM Reading Program jersey")} className="h-auto w-full transition-transform duration-500 hover:scale-110" loading="lazy" />
        </button>
        <div className="col-span-3">
          <div className="flex items-center gap-2">
            <Shirt className="h-4 w-4 text-blue-700" />
            <p className="font-display text-sm font-extrabold uppercase text-navy-900">{L("Jersi Rasmi PMM", "Official PMM Jersey")}</p>
          </div>
          <p className="mt-1 text-[11px] font-semibold text-navy-700">{L("Saiz kanak-kanak (4 - 15 tahun)", "Kids sizes (4 - 15 years)")}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {SIZES.map((s) => (
              <span key={s} className="num rounded-md bg-gradient-to-b from-blue-500 to-blue-700 px-2 py-1 text-center font-display text-[10px] font-extrabold uppercase leading-tight text-white shadow">
                {s}<span className="block text-[8px] font-bold text-yellow-200">{L("Tahun", "Yrs")}</span>
              </span>
            ))}
          </div>
          <p className="mt-2 font-script text-base text-blue-800">Baca • Ilmu • Bina Masa Depan</p>
        </div>
      </div>
    </div>
  );
}

const MINDSETS = [
  { ms: "Berfikir positif setiap hari", en: "Think positive every day" },
  { ms: "Jelaskan matlamat", en: "Set clear goals" },
  { ms: "Disiplin diri", en: "Self discipline" },
  { ms: "Belajar berterusan", en: "Keep learning" },
  { ms: "Berani melangkah", en: "Dare to step forward" },
  { ms: "Kelilingi diri dengan orang positif", en: "Surround yourself with positive people" },
  { ms: "Urus masa dengan bijak", en: "Manage time wisely" },
  { ms: "Jangan mudah menyerah", en: "Never give up easily" },
  { ms: "Jaga kesihatan mental & fizikal", en: "Care for mental & physical health" },
  { ms: "Bersyukur setiap hari", en: "Be grateful every day" },
];

// 10 Mindset Success
export function MindsetGrid({ ms, setLightbox }) {
  const L = (m, e) => (ms ? m : e);
  const poster = "https://customer-assets-jt897jd0.emergentagent.net/job_full-restore-audit-1/artifacts/9xhqop69_WhatsApp%20Image%202026-09-20%20at%207.16.17%20AM.jpeg";
  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-navy-950 to-navy-800 p-4 shadow-xl" data-testid="online-mindset-block">
      <div className="pointer-events-none absolute -bottom-12 -right-12 h-40 w-40 rounded-full bg-blue-600/30 blur-3xl" />
      <div className="relative text-center">
        <p className="font-display text-4xl font-extrabold leading-none text-transparent" style={{ backgroundImage: "linear-gradient(180deg,#f5e08d,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" }}>10</p>
        <p className="font-display text-lg font-extrabold uppercase leading-none text-white">Mindset Success!</p>
        <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-yellow-300">{L("Fikiran yang betul, hidup yang lebih hebat", "Right mindset, a greater life")}</p>
      </div>
      <ol className="relative mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
        {MINDSETS.map((m, i) => (
          <li key={m.en} className="flex items-center gap-2 rounded-lg bg-white/10 px-2 py-1.5 ring-1 ring-white/10 transition-colors hover:bg-white/15">
            <span className="num grid h-6 w-6 shrink-0 place-items-center rounded-full bg-gradient-to-br from-yellow-200 to-yellow-500 font-display text-[11px] font-extrabold text-navy-950">{i + 1}</span>
            <span className="text-[11px] font-bold uppercase leading-tight text-white">{ms ? m.ms : m.en}</span>
          </li>
        ))}
      </ol>
      <div className="relative mt-3 flex flex-col items-center gap-2">
        <p className="text-center text-xs font-semibold italic text-white/80">{L("\u201CKejayaan bermula daripada cara anda berfikir.\u201D", "\u201CSuccess begins with the way you think.\u201D")}</p>
        <button type="button" onClick={() => setLightbox(poster)} className="inline-flex items-center gap-1.5 rounded-full border border-yellow-400/60 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-yellow-300 transition-colors hover:bg-yellow-400 hover:text-navy-950" data-testid="online-mindset-poster">
          <ZoomIn className="h-3.5 w-3.5" /> {L("Lihat poster", "View poster")}
        </button>
        <Sparkles className="h-4 w-4 text-yellow-400" />
      </div>
    </div>
  );
}
